import React from 'react';
import { useSubscription } from '@/context/SubscriptionContext';
import { Sparkles } from 'lucide-react';

/**
 * TrialBanner
 *
 * Renders a non-dismissible banner at the top of every admin/provider dashboard
 * page while the user is inside their 7-day free trial. Shows the exact first-
 * charge date so there are no surprises.
 */
export default function TrialBanner() {
  const { subscription } = useSubscription();
  if (!subscription || subscription.status !== 'trialing') return null;
  if (!subscription.trial_end) return null;

  const trialEnd = new Date(subscription.trial_end);
  const now = new Date();
  const msLeft = trialEnd.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / 86400000));
  const niceDate = trialEnd.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  const amount = subscription.amount;
  const planName = subscription.plan_name;

  return (
    <div
      className="mb-5 rounded-xl border-2 border-primary/40 bg-gradient-to-r from-primary/10 via-amber-50 to-white px-4 py-3 flex items-start gap-3"
      data-testid="trial-banner"
    >
      <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
        <Sparkles className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 text-sm">
        <p className="font-semibold">
          {daysLeft === 0
            ? 'Your free trial ends today'
            : `You're on day ${7 - daysLeft + 1} of your 7-day free trial — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
        </p>
        <p className="text-xs text-muted-foreground">
          Your card will be charged
          {amount ? <> <strong>${amount}</strong></> : null}
          {planName ? <> for the <strong>{planName}</strong> plan</> : null}
          {' '}on <strong>{niceDate}</strong>. Cancel anytime from Settings → Billing before then and you won't be billed.
        </p>
      </div>
    </div>
  );
}
